import { API_BASE_URL } from '../config';
import { PERMISSIONS } from '../constants/permissions';
import { fetchWithAuth } from './fetchWithAuth';
import { getPageCache, setPageCache } from './pageCache';

export const STAFF_DEMAND_CACHE_KEY = (tenantId) => `staffDemand:v3:${tenantId}`;

/** Check cached permission list for staff demand read access.
 *  If permissions have not hydrated yet, return null (caller may still prefetch).
 *  If hydrated and missing, return false.
 */
export function userHasStaffDemandPermission() {
    try {
        const raw = localStorage.getItem('userTenantData');
        if (!raw) return null; // unknown — allow optimistic prefetch
        const tenants = JSON.parse(raw);
        if (!Array.isArray(tenants) || tenants.length === 0) return null;
        const current = localStorage.getItem('currentTenant');
        const currentId = current ? JSON.parse(current)?.id : null;
        const entry = tenants.find((t) => t.tenant_id === currentId) || tenants[0];
        const perms = entry?.permissions || [];
        return perms.includes(PERMISSIONS.STAFF_DEMAND_REPORT_READ);
    } catch {
        return null;
    }
}

/**
 * Normalize API payload into page-cache / component shape.
 */
export function normalizeStaffDemandPayload(data) {
    const customers = Array.isArray(data?.customers) ? data.customers : [];
    const customersById = {};
    customers.forEach((c) => {
        if (c.customer_id != null) customersById[String(c.customer_id)] = c;
    });

    const plansArr = Array.isArray(data?.carePlans) ? data.carePlans : [];
    const carePlans = plansArr.map((cp) => {
        const customerId = cp.customer_id;
        const customer = customerId != null ? customersById[String(customerId)] : null;
        const fullName = customer
            ? [customer.name, customer.surname].filter(Boolean).join(' ')
            : [cp.customer_name, cp.customer_surname].filter(Boolean).join(' ') || cp.customer_name;
        return {
            ...cp,
            lineItems: cp.lineItems || cp.line_items || [],
            servicePlanHours: cp.servicePlanHours || null,
            _customerId: customerId,
            _customerFullName: fullName,
        };
    });

    const sitesRaw = Array.isArray(data?.sites) ? data.sites : [];
    const sites = sitesRaw.map((s) => ({
        siteId: s.siteId ?? s.site_id,
        siteName: s.siteName ?? s.name,
    }));

    // Normalize quotation keys to strings for reliable lookup
    const quotationsRaw = data?.quotationsByCarePlanId || {};
    const quotations = {};
    Object.keys(quotationsRaw).forEach((k) => {
        quotations[String(k)] = quotationsRaw[k];
    });

    return {
        carePlans,
        quotations,
        customers,
        sites,
    };
}

/** Apply normalized cache payload via state setters (optional). */
export function applyStaffDemandPayload(normalized, setters) {
    if (!normalized || !setters) return;
    const { setAllCarePlans, setQuotationsByCarePlanId, setAllCustomers, setTenantSites } = setters;
    if (setAllCarePlans) setAllCarePlans(normalized.carePlans);
    if (setQuotationsByCarePlanId) setQuotationsByCarePlanId(normalized.quotations);
    if (setAllCustomers) setAllCustomers(normalized.customers);
    if (setTenantSites) setTenantSites(normalized.sites);
}

/**
 * Fetch staff demand report and store in pageCache.
 * Fire-and-forget safe — never throws to callers.
 */
export async function prefetchStaffDemand(tenantId, { force = false } = {}) {
    if (!tenantId) return null;
    if (!localStorage.getItem('token')) return null;

    const hasPerm = userHasStaffDemandPermission();
    if (hasPerm === false) return null;

    const cacheKey = STAFF_DEMAND_CACHE_KEY(tenantId);
    if (!force && getPageCache(cacheKey)) {
        // Quietly revalidate in background without blocking
        fetchAndCache(tenantId, cacheKey).catch(() => {});
        return getPageCache(cacheKey);
    }

    try {
        return await fetchAndCache(tenantId, cacheKey);
    } catch (e) {
        console.error('Staff demand prefetch failed:', e);
        return null;
    }
}

async function fetchAndCache(tenantId, cacheKey) {
    const res = await fetchWithAuth(
        `${API_BASE_URL}/staff-demand-report?tenant_id=${encodeURIComponent(tenantId)}`
    );
    if (!res.ok) {
        throw new Error(`staff-demand-report ${res.status}`);
    }
    const data = await res.json();
    const normalized = normalizeStaffDemandPayload(data);
    setPageCache(cacheKey, normalized);
    return normalized;
}
